import { useState } from 'react';
import { Box, Button, useTheme, useMediaQuery, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { getPaymentOptions } from 'src/api/paymentApi';
import { debounceSmall } from 'src/helpers/debounceFuncs';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import HandshakeIcon from '@mui/icons-material/HandshakeOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { isL2Claim } from 'src/helpers/claimHelper';
import { issueType } from 'src/helpers/issueType';
import { formatAmountToISKCurrencyString } from 'src/helpers/formatAmountToString';

export default function PayerClaimsActions({
  selectedClaims,
  claims,
  setOperationError,
  onIssueDrawerOpen,
  setPreSelectedIssueType,
}) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getSelectedClaims = () => {
    return claims.filter((x) => selectedClaims.includes(x.claimId));
  };

  const getTotalSelectedAmount = () => {
    const selected = getSelectedClaims();
    return selected.reduce((total, claim) => {
      return total + (claim.totalRemaining || 0);
    }, 0);
  };

  const countClaimCategories = (claimsArray) => {
    const hasLegalCollection = claimsArray.some((claim) => claim.product === 'LegalCollection');
    const hasLegalCollection2 = claimsArray.some((claim) => claim.product === 'LegalCollection2');
    const hasOtherTypes = claimsArray.some(
      (claim) => claim.product !== 'LegalCollection' && claim.product !== 'LegalCollection2'
    );

    return {
      hasLegalCollection,
      hasLegalCollection2,
      hasOtherTypes,
      categoriesCount: [hasLegalCollection, hasLegalCollection2, hasOtherTypes].filter(Boolean).length,
    };
  };

  function onGetPaymentOptionsSuccess(options, isForAgreement) {
    setOperationError(null);
    const pathname = isForAgreement ? '/semja' : '/greida';
    debounceSmall(() =>
      router.push(
        {
          pathname,
          query: {
            selected: selectedClaims,
            maxNumberOfInstallments: options.data.maxNumberOfInstallments,
            paymentMethods: options.data.paymentMethods,
            supplementaryClaimIds: options.data.supplementaryClaimIds,
            isForAgreement,
          },
        },
        pathname
      )
    );
  }

  function getPaymentOptionsForSelectedClaims(isForAgreement) {
    const selected = getSelectedClaims();
    getPaymentOptions(
      {
        claimIds: selected.map((claim) => claim.claimId),
        isForAgreement,
      },
      setIsLoading,
      onGetPaymentOptionsSuccess,
      setOperationError
    );
  }

  const getAgreementError = () => {
    const selected = getSelectedClaims();

    if (selected.length > 1) {
      const { categoriesCount } = countClaimCategories(selected);

      if (categoriesCount > 1) {
        return 'Ekki er hægt að semja um lögfræðiinnheimtu ásamt öðrum málum.';
      }
    }

    if (
      selected.every((x) => x.product === 'DebtSurveillance') ||
      selected.every((x) => x.claimantName === selected[0].claimantName)
    ) {
      return '';
    }
    return 'Þú getur greitt öll málin saman en ef þú vilt semja um afborganir þá þarf að gera samkomulag við hvern kröfuhafa fyrir sig.';
  };

  const getPaymentError = () => {
    const selected = getSelectedClaims();

    if (selected.length > 1) {
      const { categoriesCount } = countClaimCategories(selected);

      if (categoriesCount > 1) {
        return 'Ekki er hægt að greiða mál í löginnheimtu með öðrum málum.';
      }
    }

    if (selected.filter((x) => x.isPaymentAllowed).length !== selectedClaims.length) {
      return 'Val inniheldur mál sem ekki er hægt að greiða';
    }
    return '';
  };

  function handleAgreementClick() {
    const selected = getSelectedClaims();
    if (isL2Claim(selected[0].claimId)) {
      setPreSelectedIssueType(issueType.PayerAgreementRequest);
      onIssueDrawerOpen(true);
    } else {
      getPaymentOptionsForSelectedClaims(true);
    }
  }

  const hasValidSelection = selectedClaims && selectedClaims.length > 0;
  const agreementError = getAgreementError();
  const paymentError = getPaymentError();
  const totalAmount = getTotalSelectedAmount();

  const displayError = paymentError || agreementError;

  return (
    <Box
      display={'flex'}
      flexDirection={isMobile ? 'column' : 'row'}
      justifyContent={isMobile ? 'flex-end' : 'space-between'}
      alignItems={isMobile ? 'stretch' : 'center'}
      sx={{ mt: 5, mb: 4 }}
    >
      <Box
        sx={{
          flex: isMobile ? 'none' : '1',
          display: 'flex',
          justifyContent: 'flex-start',
          order: isMobile ? 3 : 1,
          minHeight: 7,
        }}
      >
        <Box
          sx={{
            backgroundColor: displayError ? '#FAE0A799' : 'transparent',
            padding: '18px 16px',
            maxWidth: isMobile ? '100%' : 'inherit',
            minWidth: displayError ? '200px' : '0px',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRadius: 1,
            visibility: displayError ? 'visible' : 'hidden',
            opacity: displayError ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
          }}
        >
          <WarningAmberIcon
            sx={{
              fontSize: '24px',
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: '16px',
              fontWeight: 400,
              margin: 0,
              lineHeight: 1.4,
            }}
          >
            {displayError || 'Placeholder text to maintain height'}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-end',
          order: isMobile ? 2 : 2,
          minHeight: '56px',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: '16px',
            fontWeight: 450,
            whiteSpace: 'nowrap',
            opacity: hasValidSelection ? 1 : 0.5,
            transition: 'opacity 0.2s ease-in-out',
          }}
        >
          Samtals valið: {formatAmountToISKCurrencyString(totalAmount)}
        </Typography>
      </Box>

      <Box
        display={'flex'}
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="flex-end"
        sx={{
          flexShrink: 0,
          order: isMobile ? 1 : 3,
          mb: isMobile ? 2 : 0,
          pl: isMobile ? 0 : 4,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            width: isMobile ? '100%' : 'auto',
            marginBottom: isMobile ? '16px' : '0',
          }}
        >
          <Button
            size="large"
            variant="outlined"
            onClick={() => getPaymentOptionsForSelectedClaims(false)}
            disabled={!hasValidSelection || !!paymentError || isLoading}
            startIcon={<PaymentsIcon />}
            sx={{ mr: isMobile ? 0 : 2, backgroundColor: '#FFFFFF', width: isMobile ? '100%' : 'auto' }}
          >
            Greiða öll mál
          </Button>
        </div>

        <div style={{ display: 'inline-block', width: isMobile ? '100%' : 'auto' }}>
          <Button
            size="large"
            variant="outlined"
            onClick={handleAgreementClick}
            disabled={!hasValidSelection || !!agreementError || isLoading}
            startIcon={<HandshakeIcon />}
            sx={{ backgroundColor: '#FFFFFF', width: isMobile ? '100%' : 'auto' }}
          >
            Semja um öll mál
          </Button>
        </div>
      </Box>
    </Box>
  );
}
